import { useEffect, useState } from 'react';
import { db } from '../../Components/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import '../../Components/Css/MainFilterPanel.css';

const FilterPanel = ({ searchQuery = '', onFilteredPosts }) => {
  const [allPosts, setAllPosts] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [dateFilter, setDateFilter] = useState('Show All');
  const [sortBy, setSortBy] = useState('Show All');
  const [selectedPostTypes, setSelectedPostTypes] = useState([]);

  const [sectionsOpen, setSectionsOpen] = useState({
    location: true,
    postTypes: true,
    date: true,
    sort: true,
  });

    // 🔹 Fetch BOOKMARKED posts only
  useEffect(() => {
    const fetchBookmarkedPosts = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          setAllPosts([]);
          return;
        }

        const userRef = doc(db, 'Users', user.uid);
        const userSnap = await getDoc(userRef);

        const bookmarkedIds =
          (userSnap.exists() && userSnap.data().bookmarks) || [];

        if (bookmarkedIds.length === 0) {
          setAllPosts([]);
          return;
        }

        const postsRef = collection(db, 'Posts');
        const bookmarkedPosts = [];

        const chunkSize = 10; // Firestore "in" limit
        for (let i = 0; i < bookmarkedIds.length; i += chunkSize) {
          const chunk = bookmarkedIds.slice(i, i + chunkSize);
          const q = query(postsRef, where('__name__', 'in', chunk));
          const snap = await getDocs(q);

          snap.forEach(doc => {
            bookmarkedPosts.push({
              id: doc.id,
              ...doc.data(),
            });
          });
        }

        setAllPosts(bookmarkedPosts);

        // 🔹 Extract locations from bookmarked posts
        const locations = Array.from(
          new Set(bookmarkedPosts.map(p => p.location).filter(Boolean))
        );
        setAvailableLocations(locations);
      } catch (err) {
        console.error('Error fetching bookmarked posts:', err);
        setAllPosts([]);
      }
    };

    fetchBookmarkedPosts();
  }, []);

  // 🔹 Apply filters + sorting
  useEffect(() => {
    let filtered = [...allPosts];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        (post.title || '').toLowerCase().includes(q) ||
        (post.description || '').toLowerCase().includes(q)
      );
    }

    // Post Locations (Show All = none selected)
    if (selectedLocations.length > 0) {
      filtered = filtered.filter(p =>
        selectedLocations.includes(p.location)
      );
    }

    // Post Types filter (Show All = none selected)
    if (selectedPostTypes.length > 0) {
      filtered = filtered.filter(p =>
        selectedPostTypes.includes((p.type || '').toLowerCase())
      );
    }

    // Date filter
    if (dateFilter !== 'Show All') {
      const now = new Date();
      filtered = filtered.filter(p => {
        const postDate = new Date(p.createdAt?.seconds * 1000);
        if (dateFilter === 'Today') {
          return postDate.toDateString() === now.toDateString();
        }
        if (dateFilter === 'This Week') {
          return now - postDate <= 7 * 86400000;
        }
        if (dateFilter === 'This Month') {
          return (
            postDate.getMonth() === now.getMonth() &&
            postDate.getFullYear() === now.getFullYear()
          );
        }
        if (dateFilter === 'Last Three Months') {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        return postDate >= threeMonthsAgo && postDate <= now;
        }
        return true;
      });
    }

  

    // Sorting
    if (sortBy === 'Newest') {
      filtered.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
    }
    if (sortBy === 'Oldest') {
      filtered.sort((a, b) => a.createdAt?.seconds - b.createdAt?.seconds);
    }
    if (sortBy === 'Most Liked') {
    filtered.sort(
    (a, b) =>
      (b.likes?.length || 0) - (a.likes?.length || 0)
    );
}

    onFilteredPosts(filtered);
  }, [
    searchQuery,
    selectedLocations,
    dateFilter,
    sortBy,
    allPosts,
    onFilteredPosts,
  ]);

  const toggleSection = (key) => {
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="filterpanel">

      {/* POST LOCATIONS */}
      <div className="filterpanel-section">
        <div className="filterpanel-header" onClick={() => toggleSection('location')}>
          Post Locations <span>{sectionsOpen.location ? '−' : '+'}</span>
        </div>

        {sectionsOpen.location && (
          <div className="filterpanel-options">

            {/* SHOW ALL */}
            <label className="filterpanel-option">
              <input
                type="checkbox"
                checked={selectedLocations.length === 0}
                onChange={() => setSelectedLocations([])}
              />
              Show All
            </label>

            {/* FIRESTORE LOCATIONS */}
            {availableLocations.map(loc => (
              <label key={loc} className="filterpanel-option">
                <input
                  type="checkbox"
                  checked={selectedLocations.includes(loc)}
                  onChange={() =>
                    setSelectedLocations(prev =>
                      prev.includes(loc)
                        ? prev.filter(l => l !== loc)
                        : [...prev, loc]
                    )
                  }
                />
                {loc}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* DATE */}
      <div className="filterpanel-section">
        <div className="filterpanel-header" onClick={() => toggleSection('date')}>
          Date Posted <span>{sectionsOpen.date ? '−' : '+'}</span>
        </div>
        {sectionsOpen.date && (
          <div className="filterpanel-options">
            {['Show All', 'Today', 'This Week', 'This Month', 'Last Three Months'].map(opt => (
              <label key={opt} className="filterpanel-option">
                <input
                  type="radio"
                  checked={dateFilter === opt}
                  onChange={() => setDateFilter(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* SORT */}
      <div className="filterpanel-section">
        <div className="filterpanel-header" onClick={() => toggleSection('sort')}>
          Sort By <span>{sectionsOpen.sort ? '−' : '+'}</span>
        </div>
        {sectionsOpen.sort && (
          <div className="filterpanel-options">
            {['Show All', 'Newest', 'Oldest', 'Most Liked'].map(opt => (
              <label key={opt} className="filterpanel-option">
                <input
                  type="radio"
                  checked={sortBy === opt}
                  onChange={() => setSortBy(opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* POST TYPES */}
      <div className="filterpanel-section">
        <div
          className="filterpanel-header"
          onClick={() => toggleSection('postTypes')}
        >
          Post Types <span>{sectionsOpen.postTypes ? '−' : '+'}</span>
        </div>

        {sectionsOpen.postTypes && (
          <div className="filterpanel-options">

            {/* SHOW ALL */}
            <label className="filterpanel-option">
              <input
                type="checkbox"
                checked={selectedPostTypes.length === 0}
                onChange={() => setSelectedPostTypes([])}
              />
              Show All
            </label>

            {[
              { label: "Video", value: "video" },
              { label: "Blog", value: "blog" },
              { label: "Event", value: "event" },
              { label: "Request", value: "request" },
              { label: "Market", value: "market" },
              { label: "Directory", value: "directory" },
              { label: "Trucks", value: "trucks" },
              { label: "Vehicle", value: "vehicle" },
              { label: "Loads", value: "loads" },
              { label: "Offer", value: "offer" },
            ].map(item => (
              <label key={item.value} className="filterpanel-option">
                <input
                  type="checkbox"
                  checked={selectedPostTypes.includes(item.value)}
                  onChange={() =>
                    setSelectedPostTypes(prev =>
                      prev.includes(item.value)
                        ? prev.filter(t => t !== item.value)
                        : [...prev, item.value]
                    )
                  }
                />
                {item.label}
              </label>
            ))}

          </div>
        )}
      </div>

    </div>
  );
};

export default FilterPanel;
